import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { edgeAccountDigestsApi } from '../../api/services';
import type { DigestSettings, DigestSettingsType, DigestTypeSetting } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import { renderSubjectPreview, sampleTokenValues } from './digestSubjectPreview';
import { DIGEST_SETTINGS_QUERY_KEY, useDigestSettings } from '../../hooks/useDigestSettings';

// Classifier order (first match wins on the server): this is also the order a
// digest's sections are checked to pick its subject.
const TYPE_ROWS: Array<{ type: DigestSettingsType; label: string; hint: string }> = [
  { type: 'restock', label: 'Restock', hint: 'Used when the digest recommends items to reorder.' },
  { type: 'oos', label: 'Out of stock', hint: 'Used when nothing needs reordering but something is out of stock.' },
  { type: 'all_stocked', label: 'All stocked', hint: 'Used on a restock day when nothing needs reordering.' },
  { type: 'usage', label: 'Usage', hint: 'Used for everything else, such as daily usage only.' },
];

type Draft = Record<DigestSettingsType, DigestTypeSetting>;
type FieldErrors = Record<string, string[]>;

const toDraft = (settings: DigestSettings): Draft =>
  TYPE_ROWS.reduce((acc, { type }) => {
    acc[type] = { enabled: settings.types[type].enabled, subject: settings.types[type].subject };
    return acc;
  }, {} as Draft);

const formatUpdatedAt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

interface DigestSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

// Global digest email settings (sc-6504): one subject template and one on/off
// switch per digest type. Turning a type off stops that section for every
// location (it can only turn things off; location preferences still apply).
export const DigestSettingsDialog: React.FC<DigestSettingsDialogProps> = ({ open, onClose, onSaved }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [confirmRestockOff, setConfirmRestockOff] = React.useState(false);
  const inputRefs = React.useRef<Partial<Record<DigestSettingsType, HTMLInputElement | null>>>({});
  const sampleValues = React.useMemo(() => sampleTokenValues(), []);

  const settingsQuery = useDigestSettings({ enabled: open });

  // Re-seed from the saved settings each time the dialog opens, so a cancelled
  // edit never leaks into the next open.
  React.useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setServerError(null);
    if (settingsQuery.data) setDraft(toDraft(settingsQuery.data));
  }, [open, settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (types: Draft) => edgeAccountDigestsApi.settings.update({ types }),
    onSuccess: saved => {
      queryClient.setQueryData(DIGEST_SETTINGS_QUERY_KEY, saved);
      onSaved?.();
      onClose();
    },
    onError: err => {
      const errors = (err as AxiosError<{ errors?: unknown }>).response?.data?.errors;
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        setFieldErrors(errors as FieldErrors);
      } else {
        setServerError(extractApiError(err, 'Failed to save digest email settings'));
      }
    },
  });

  const updateType = (type: DigestSettingsType, patch: Partial<DigestTypeSetting>) =>
    setDraft(current => (current ? { ...current, [type]: { ...current[type], ...patch } } : current));

  const handleToggle = (type: DigestSettingsType, enabled: boolean) => {
    if (type === 'restock' && !enabled) {
      setConfirmRestockOff(true);
      return;
    }
    updateType(type, { enabled });
  };

  // Insert `{{token}}` at the cursor (replacing any selection), then put the
  // cursor right after it.
  const insertToken = (type: DigestSettingsType, token: string) => {
    if (!draft) return;
    const text = `{{${token}}}`;
    const input = inputRefs.current[type];
    const current = draft[type].subject;
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? current.length;
    updateType(type, { subject: current.slice(0, start) + text + current.slice(end) });
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const submit = () => {
    if (!draft) return;
    setFieldErrors({});
    setServerError(null);
    saveMutation.mutate(draft);
  };

  const saved = settingsQuery.data;
  const busy = saveMutation.isPending;

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="digest-settings-dialog-title"
      >
        <DialogTitle id="digest-settings-dialog-title">Digest email settings</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Each digest gets the subject for the first type that applies, in the order below. Turning a
            type off stops it for every location.
          </Typography>
          <Alert severity="warning" data-testid="digest-settings-draft-warning">
            Turning a type off also removes that section from existing draft digests the next time they
            are refreshed. Turning it back on does not restore it to those drafts.
          </Alert>

          {serverError && <Alert severity="error">{serverError}</Alert>}
          {fieldErrors.types && <Alert severity="error">{fieldErrors.types.join('. ')}</Alert>}
          {settingsQuery.error && (
            <Alert severity="error">
              {extractApiError(settingsQuery.error, 'Failed to load digest email settings')}
            </Alert>
          )}

          {settingsQuery.isLoading || !draft || !saved ? (
            !settingsQuery.error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )
          ) : (
            TYPE_ROWS.map(({ type, label, hint }) => {
              const row = draft[type];
              const subjectErrors = fieldErrors[`${type}.subject`];
              const typeErrors = [...(fieldErrors[type] ?? []), ...(fieldErrors[`${type}.enabled`] ?? [])];
              return (
                <Box
                  key={type}
                  data-testid={`digest-type-${type}`}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" component="h3">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{hint}</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={row.enabled}
                          onChange={e => handleToggle(type, e.target.checked)}
                          inputProps={{ 'aria-label': `${label} enabled` }}
                        />
                      }
                      label={row.enabled ? 'On' : 'Off'}
                    />
                  </Box>
                  <TextField
                    label="Subject"
                    size="small"
                    fullWidth
                    value={row.subject}
                    onChange={e => updateType(type, { subject: e.target.value })}
                    inputRef={el => { inputRefs.current[type] = el; }}
                    error={Boolean(subjectErrors)}
                    helperText={subjectErrors?.join('. ')}
                    inputProps={{ 'aria-label': `${label} subject` }}
                  />
                  {typeErrors.length > 0 && <Alert severity="error">{typeErrors.join('. ')}</Alert>}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {saved.tokens.map(token => (
                      <Chip
                        key={token}
                        size="small"
                        variant="outlined"
                        label={`{{${token}}}`}
                        onClick={() => insertToken(type, token)}
                        aria-label={`Insert ${token} into ${label} subject`}
                      />
                    ))}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{ ml: 'auto' }}
                      onClick={() => updateType(type, { subject: saved.types[type].default_subject })}
                      aria-label={`Reset ${label} subject to default`}
                    >
                      Reset to default
                    </Link>
                  </Box>
                  <Typography variant="body2" color="text.secondary" data-testid={`digest-preview-${type}`}>
                    Preview: {renderSubjectPreview(row.subject, sampleValues)}
                  </Typography>
                </Box>
              );
            })
          )}

          {saved?.updated_by_email && (
            <Typography variant="caption" color="text.secondary">
              Last changed by {saved.updated_by_email} · {formatUpdatedAt(saved.updated_at)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || !draft || Boolean(settingsQuery.error)}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestockOff} onClose={() => setConfirmRestockOff(false)} aria-labelledby="restock-off-title">
        <DialogTitle id="restock-off-title">Turn off restock digests?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stops all restock recommendations and auto-quotes for every location. Restock is also
            removed from existing drafts when they are next refreshed, and turning it back on does not
            restore it to those drafts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestockOff(false)}>Keep on</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              updateType('restock', { enabled: false });
              setConfirmRestockOff(false);
            }}
          >
            Turn off
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
